import { useEffect, useRef, useState } from "react";
import { Button, DatePicker, Space, Spin, Tag } from "antd";
import { SyncOutlined, UserOutlined, PhoneOutlined, MobileOutlined, CalendarOutlined, IdcardOutlined, BuildOutlined, SearchOutlined, BankOutlined } from "@ant-design/icons";
import * as echarts from "echarts";
import moment from "moment";

import SimCardTrafficApi from "../../../api/SimCardTrafficApi";
import StringUtils from "../../../util/StringUtils";
import {
    SimCardAccountWrapper,
    ToolBarWrapper,
    ToolBarLeftWrapper,
    ToolBarRightWrapper,
    AccountCardWrapper,
    AccountCard,
    AccountCardHeader,
    AccountHeaderLeft,
    ProviderIcon,
    AccountTitle,
    AccountSubtitle,
    BalanceDisplay,
    BalanceLabel,
    BalanceValue,
    AccountCardBody,
    InfoGrid,
    InfoItem,
    InfoItemLabel,
    InfoItemValue,
    Divider,
    AccountCardFooter,
    LoadingWrapper,
    ChartsWrapper,
    ChartCard,
    ChartTitle,
    DateRangeWrapper,
    ChartContainer
} from "./SimCardAccount.styled";
import dayjs from "dayjs";

const SimCardAccount: React.FC = () => {
    let simCardTrafficApi = new SimCardTrafficApi();

    let [account, setAccount] = useState<any>(null);
    let [loading, setLoading] = useState(true);

    let now = moment();
    let defaultStartTime = now.clone().subtract(1, 'year').startOf('day').valueOf();
    let defaultEndTime = now.clone().endOf('day').valueOf();
    let [startTime, setStartTime] = useState(defaultStartTime);
    let [endTime, setEndTime] = useState(defaultEndTime);

    let lineChartRef = useRef<HTMLDivElement>(null);
    let lineChartInstance = useRef<echarts.ECharts | null>(null);

    let loadAccount = async () => {
        setLoading(true);
        try {
            let response = await simCardTrafficApi.getLatestSimAccount();
            let data = response.data.data;
            setAccount(data);
            loadHistoryData();
        } catch (error) {
            console.error("Failed to load sim account:", error);
        } finally {
            setLoading(false);
        }
    };
    
    let loadHistoryData = async () => {
        if (!startTime || !endTime) return;

        let start = startTime;
        let end = endTime;
        let historyResponse = await simCardTrafficApi.queryHistorySimAccount(start, end);
        let data = historyResponse.data.data;
        renderLineChart(data);
    };

    let renderLineChart = (data: any[]) => {
        if (!lineChartRef.current) return;

        if (!lineChartInstance.current) {
            lineChartInstance.current = echarts.init(lineChartRef.current);
        }

        let formatTimestampAxis = (value)=> {
            let text = moment(Number(value)).format('YYYY/MM/DD HH:mm:ss');
            text=text.replace(' ', '\n');
            return text;
        }

        let seriesData = data.map(item => {
            return {
                name: item.timestamp + '',
                value: [Number(item.timestamp), Number(item.balance)],
            };
        });

        let option = {
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    animation: false
                },
                confine: true,
                backgroundColor: "rgba(0,0,0,0.7)",
                formatter: function(params) {
                    if (params == null) {
                        return '';
                    }

                    let seriesContent = '';
                    for(let param of params) {
                        if (param.value==null) {
                            continue;
                        }

                        seriesContent += `
                            <div style="color: #EEEEEE;">
                                <div style="padding: 5px 0px;">
                                    <span style="display: inline-block; width: 50px;">余额:</span> 
                                    <span style="display: inline-block">${param.value[1].toFixed(1)} 元</span>
                                </div>
                                <div>
                                    <span style="display: inline-block; width: 50px;">时间:</span> 
                                    <span style="display: inline-block">${moment(param.value[0]).format('YYYY-MM-DD HH:mm:ss')}</span>
                                </div>
                            </div>
                        `;
                        break;
                    }
                    let content=`
                        <div style="width: 220px;">
                            ${seriesContent}
                        </div>
                    `;
                    return content;
                },
            },
            grid: {
                left: 10,
                right: 0,
                bottom: 40,
                top: 40,
                containLabel: true
            },
            xAxis: {
                type: 'category',
                minorTick: {
                    show: true
                },
                minorSplitLine: {
                    show: true
                },
                splitLine: {
                    show: true,
                },
                splitNumber: 2,
                axisLabel: {
                    formatter: function (value, index) {
                        return formatTimestampAxis(value);
                    }
                },
            },
            yAxis: {
                type: 'value',
                name: '余额(元)',
                boundaryGap: [0, '100%'],
                axisLine: {
                    show: true
                },
                minorTick: {
                    show: true
                },
                minorSplitLine: {
                    show: true
                },
                splitLine: {
                    show: true,
                },
            },
            series: [{
                name: '余额',
                type: 'line',
                smooth: true,
                showSymbol: false,
                data: seriesData,
                lineStyle: {
                    color: '#1890ff',
                    width: 2
                },
                areaStyle: {
                    color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                        { offset: 0, color: 'rgba(24, 144, 255, 0.3)' },
                        { offset: 1, color: 'rgba(24, 144, 255, 0.05)' }
                    ])
                },
                itemStyle: {
                    color: '#1890ff'
                }
            }]
        };

        lineChartInstance.current.setOption(option);
    };

    let onQuery = () => {
        loadHistoryData();
    };

    let getProviderColor = (provider: string) => {
        switch (StringUtils.nonull(provider).toUpperCase()) {
            case 'SIMBOSS':
                return '#1890ff';
            case 'SICONG':
                return '#52c41a';
            case 'GLOBAL':
                return '#faad14';
            default:
                return '#1890ff';
        }
    };

    let formatBalance = (balance: number) => {
        if (balance == null || isNaN(balance)) return '0.00';
        return `¥ ${Number(balance).toFixed(2)}`;
    };

    useEffect(() => {
        loadAccount();
    }, []);

    if (loading) {
        return (
            <SimCardAccountWrapper>
                <ToolBarWrapper>
                    <ToolBarLeftWrapper>
                        <Space size="middle">
                            <Button type="primary" icon={<SyncOutlined />} onClick={loadAccount}>刷新</Button>
                        </Space>
                    </ToolBarLeftWrapper>
                </ToolBarWrapper>
                <LoadingWrapper>
                    <Spin size="large" tip="加载中...">
                        <div style={{ height: 200 }} />
                    </Spin>
                </LoadingWrapper>
            </SimCardAccountWrapper>
        );
    }

    if (!account) {
        return (
            <SimCardAccountWrapper>
                <ToolBarWrapper>
                    <ToolBarLeftWrapper>
                        <Space size="middle">
                            <Button type="primary" icon={<SyncOutlined />} onClick={loadAccount}>刷新</Button>
                        </Space>
                    </ToolBarLeftWrapper>
                </ToolBarWrapper>
                <AccountCardWrapper>
                    <AccountCard>
                        <AccountCardBody style={{ textAlign: 'center', padding: '60px 32px' }}>
                            <p style={{ color: '#8c8c8c' }}>暂无账户信息</p>
                        </AccountCardBody>
                    </AccountCard>
                </AccountCardWrapper>
            </SimCardAccountWrapper>
        );
    }

    return (
        <SimCardAccountWrapper>
            <ToolBarWrapper>
                <ToolBarLeftWrapper>
                    <Space size="middle">
                        <Button type="primary" icon={<SyncOutlined />} onClick={loadAccount}>刷新</Button>
                    </Space>
                </ToolBarLeftWrapper>
            </ToolBarWrapper>

            <AccountCardWrapper>
                <AccountCard>
                    <AccountCardHeader style={{ background: `linear-gradient(135deg, ${getProviderColor(account.provider)} 0%, ${getProviderColor(account.provider)}dd 100%)` }}>
                        <AccountHeaderLeft>
                            <ProviderIcon>
                                <UserOutlined />
                            </ProviderIcon>
                            <div>
                                <AccountTitle>{StringUtils.nonull(account.provider || '未知供应商')}</AccountTitle>
                                <AccountSubtitle>{StringUtils.nonull(account.name)}</AccountSubtitle>
                            </div>
                        </AccountHeaderLeft>
                        <BalanceDisplay>
                            <BalanceLabel>账户余额</BalanceLabel>
                            <BalanceValue>{formatBalance(account.balance)}</BalanceValue>
                        </BalanceDisplay>
                    </AccountCardHeader>

                    <AccountCardBody>
                        <InfoGrid>
                            <InfoItem>
                                <InfoItemLabel><BuildOutlined /> 公司名称</InfoItemLabel>
                                <InfoItemValue>{StringUtils.nonull(account.company)}</InfoItemValue>
                            </InfoItem>

                            <InfoItem>
                                <InfoItemLabel><UserOutlined /> 账户名称</InfoItemLabel>
                                <InfoItemValue>{StringUtils.nonull(account.name)}</InfoItemValue>
                            </InfoItem>

                            <InfoItem>
                                <InfoItemLabel><MobileOutlined /> 联系电话</InfoItemLabel>
                                <InfoItemValue>{account.mobile}</InfoItemValue>
                            </InfoItem>

                            
                            <InfoItem>
                                <InfoItemLabel><CalendarOutlined /> 更新时间</InfoItemLabel>
                                <InfoItemValue>{account.timestamp ? moment(Number(account.timestamp)).format("YYYY-MM-DD HH:mm:ss") : 'N/A'}</InfoItemValue>
                            </InfoItem>
                        </InfoGrid>
                    </AccountCardBody>
                </AccountCard>
            </AccountCardWrapper>

            <ChartsWrapper>
                <ChartCard>
                    <ChartTitle>余额趋势</ChartTitle>
                    <DateRangeWrapper>
                        <DatePicker
                            showTime
                            value={dayjs(startTime)}
                            onChange={(date) => setStartTime(date.toDate().getTime())}
                            placeholder="开始时间"
                        />
                        <span style={{ color: '#999' }}>-</span>
                        <DatePicker
                            showTime
                            value={dayjs(endTime)}
                            onChange={(date) => setEndTime(date.toDate().getTime())}
                            placeholder="结束时间"
                        />
                        <Button type="primary" size="small" icon={<SearchOutlined />} onClick={onQuery} style={{marginLeft: '20px'}}>查询</Button>
                    </DateRangeWrapper>
                    <ChartContainer ref={lineChartRef} />
                </ChartCard>
            </ChartsWrapper>
        </SimCardAccountWrapper>
    );
};

export default SimCardAccount;